/**
 * KPIDriversDisplay Component
 *
 * A shared component for displaying and editing KPI driver dimensions.
 * Supports readonly, dimensionSelect (inline dropdown), and dimensionPicker (dialog-based) modes.
 *
 * @example
 * // Readonly mode (PageSidePanel) — pass the entry for the dataset being displayed
 * <KPIDriversDisplay driverOptions={currentPageDriverOptions?.[datasetSource]} renderMode="readonly" />
 *
 * // Editable with inline dropdowns (PageSetupWizard)
 * <KPIDriversDisplay driverOptions={options} renderMode="dimensionSelect" hideSwitches onChange={handleChange} />
 *
 * // Interactive with switches and dialog (Deckline drivers)
 * <KPIDriversDisplay driverOptions={options} renderMode="dimensionPicker" onToggleSwitch={handleToggle} onChange={handleChange} />
 */

import './KPIDriversDisplay.scss';

import {
  DimensionPickerDialogWithProvider,
  DimensionPickerDialogWithProviderProps,
  DimensionPickerDropdown,
  DimensionPickerDropdownProps,
} from '@amzn/gft-dimension-picker';
import { DimSumEnhancedNode } from '@amzn/gft-dimsum-service';
import { KPI_DIMENSION_CONFIG_MAP, KPI_DIMENSION_RENDER_MAP } from '@constants/page';
import EditIcon from '@mui/icons-material/Edit';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import { DatasetSource } from '@services/api/deckManagement/graphql/schema/API';
import { convertDimSumNodeToKpiDriverValues, convertKpiDriverValuesToDimSumNodeId } from '@utils/kpiDriverHelpers';
import { isNil } from 'lodash';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import { DriverDisplayOptionEntry, DriverDisplayOptionMap } from 'types/PageContext.types';
import {
  AnyKPIDrivers,
  BaseKpiDimensionFieldTypes,
  BaseKpiDimensionFieldTypeSelect,
  KpiDimension,
  KpiDimensionConfig,
  KpiDriverValues,
} from 'types/PageSidePanel.types';

export interface KPIDriversDisplayProps {
  /** Selects which dataset's dimension render list to display (from KPI_DIMENSION_RENDER_MAP) */
  datasetSource: DatasetSource;
  /** The driver display option map to render */
  driverOptions: DriverDisplayOptionMap;
  /** Controls the rendering and interaction mode */
  renderMode: 'readonly' | 'dimensionSelect' | 'dimensionPicker';
  /** When true, hides toggle switches. Implicit when renderMode is "readonly". */
  hideSwitches?: boolean;
  /** Dimensions to exclude from rendering */
  excludeDimensions?: KpiDimension[];
  /** Disables all interactive controls without hiding them */
  disabled?: boolean;
  /** Additional CSS class name(s) for the root element */
  className?: string;
  /** Optional driver set to display as placeholder values when a dimension's switch is off */
  placeholderDrivers?: AnyKPIDrivers | null;
  /**
   * Fired when a dimension value changes (via dropdown selection or dialog confirmation).
   * `updates` is a dataset-agnostic {@link KpiDriverValues} bag so HCTR-only dimensions
   * (measure/transferDirection/workflow) flow through alongside the shared Galaxy dimensions.
   */
  onChange?: (key: KpiDimension, updates: KpiDriverValues, updatedDriverMap: DriverDisplayOptionMap) => void;
  /** Fired when a dimension's toggle switch is changed */
  onToggleSwitch?: (key: KpiDimension, checked: boolean) => void;
}

const BASE_CLASS = 'kpi-drivers-display';

const KPIDriversDisplay = ({
  datasetSource,
  driverOptions,
  renderMode,
  hideSwitches,
  excludeDimensions,
  disabled,
  className,
  placeholderDrivers,
  onChange,
  onToggleSwitch,
}: KPIDriversDisplayProps) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogDimension, setDialogDimension] = useState('');
  const [dialogRootNodes, setDialogRootNodes] = useState<string[] | null>(null);
  const [editingKey, setEditingKey] = useState<KpiDimension | null>(null);

  /**
   * Filtered list of dimension configs to render, excluding dimensions
   * specified in `excludeDimensions` and those without data in `driverOptions`.
   */
  const dimensionList = useMemo(
    () =>
      KPI_DIMENSION_RENDER_MAP[datasetSource].filter(
        (config) => !excludeDimensions?.includes(config.key) && driverOptions[config.key],
      ),
    [datasetSource, excludeDimensions, driverOptions],
  );

  /**
   * Builds the root element className string based on component state.
   * Combines the base class with state modifiers and any consumer-provided className.
   */
  const rootClassName = useMemo(() => {
    const classes = [BASE_CLASS, renderMode];
    if (disabled) classes.push('disabled');
    if (className) classes.push(className);
    return classes.join(' ');
  }, [disabled, renderMode, className]);

  /**
   * Handles a DimensionPickerDropdown selection change in `dimensionSelect` mode.
   * Converts the selected DimSum node into GalaxyKPIDrivers updates and notifies the consumer.
   * @param dimensionConfig - The KPI dimension configuration for the changed dimension
   * @param node - The selected DimSum node, or null if cleared
   */
  const handleDropdownChange = useCallback(
    (dimensionConfig: KpiDimensionConfig, node: DimSumEnhancedNode | null) => {
      const existingEntry = driverOptions[dimensionConfig.key];
      if (!existingEntry) return;

      const updates = convertDimSumNodeToKpiDriverValues(node, dimensionConfig);
      const updatedEntry: DriverDisplayOptionEntry = {
        ...existingEntry,
        value: node?.attributes?.name ?? null,
        hierarchy: node?.nodeID ? node.nodeID.split('-')[0] : null,
      };
      const updatedMap: DriverDisplayOptionMap = { ...driverOptions, [dimensionConfig.key]: updatedEntry };
      onChange?.(dimensionConfig.key, updates, updatedMap);
    },
    [driverOptions, onChange],
  );

  /**
   * Handles a Select input value change for Select-type dimensions (e.g., Cost Type).
   * Maps the selected value to the appropriate GalaxyKPIDrivers field and notifies the consumer.
   * @param dimensionConfig - The KPI dimension configuration for the changed dimension
   * @param event - The MUI SelectChangeEvent containing the new value
   */
  const handleSelectChange = useCallback(
    (dimensionConfig: KpiDimensionConfig, event: SelectChangeEvent) => {
      const { value } = event.target;
      const { decklineDrivers } = dimensionConfig;
      if (!decklineDrivers) return;

      const updates: KpiDriverValues = { [decklineDrivers.valueAccessor]: value };
      const existingEntry = driverOptions[dimensionConfig.key];
      if (!existingEntry) return;

      const updatedEntry: DriverDisplayOptionEntry = { ...existingEntry, value };
      const updatedMap: DriverDisplayOptionMap = { ...driverOptions, [dimensionConfig.key]: updatedEntry };
      onChange?.(dimensionConfig.key, updates, updatedMap);
    },
    [driverOptions, onChange],
  );

  /**
   * Opens the DimensionPickerDialog for a given dimension when the edit button is clicked.
   * Only applicable for dimensions with `BaseKpiDimensionFieldTypes.DimensionPicker` field type.
   * @param dimensionConfig - The KPI dimension configuration to open the dialog for
   */
  const handleEditClick = useCallback((dimensionConfig: KpiDimensionConfig) => {
    if (dimensionConfig.baseKpiField.type === BaseKpiDimensionFieldTypes.DimensionPicker) {
      setEditingKey(dimensionConfig.key);
      setDialogDimension(dimensionConfig.baseKpiField.dimensionName);
      setDialogRootNodes(
        dimensionConfig.baseKpiField.rootNodesFilter ? dimensionConfig.baseKpiField.rootNodesFilter : null,
      );
      setDialogOpen(true);
    }
  }, []);

  /**
   * Handles the DimensionPickerDialog confirmation when a user selects a node.
   * Extracts hierarchy and value from the selected node, computes GalaxyKPIDrivers updates,
   * and notifies the consumer via `onChange`.
   * @param selectedNodes - Array of selected DimSum nodes (single-select; uses first element)
   */
  const handleDialogConfirm = useCallback(
    (selectedNodes: DimSumEnhancedNode[]) => {
      if (isNil(editingKey)) return;

      const existingEntry = driverOptions[editingKey];
      if (!existingEntry) return;

      const dimensionConfig = KPI_DIMENSION_CONFIG_MAP[editingKey];
      const selectedNode = selectedNodes[0];
      const updates = convertDimSumNodeToKpiDriverValues(selectedNode, dimensionConfig);

      const [hierarchyValue] = selectedNode.nodeID.split('-');
      const updatedEntry: DriverDisplayOptionEntry = {
        ...existingEntry,
        value: selectedNode.attributes.name,
        hierarchy: hierarchyValue,
      };
      const updatedMap: DriverDisplayOptionMap = { ...driverOptions, [editingKey]: updatedEntry };

      onChange?.(editingKey, updates, updatedMap);
      setDialogOpen(false);
      setEditingKey(null);
    },
    [driverOptions, editingKey, onChange],
  );

  /**
   * Handles the DimensionPickerDialog close/cancel action.
   * Resets dialog state without triggering any value changes.
   */
  const handleDialogClose = useCallback(() => {
    setDialogOpen(false);
    setEditingKey(null);
  }, []);

  /**
   * Renders a readonly dimension row as a ListItem with label/value text.
   * Used when the component is in `readonly` mode.
   * @param config - The KPI dimension configuration
   * @param entry - The driver display option entry containing the value to display
   */
  const renderReadonlyRow = useCallback(
    (config: KpiDimensionConfig, entry: DriverDisplayOptionEntry) => (
      <ListItem key={config.key} disablePadding>
        <ListItemText
          primary={config.label}
          secondary={entry.value || ''}
          slotProps={{ primary: { variant: 'body2' }, secondary: { variant: 'body1' } }}
        />
      </ListItem>
    ),
    [],
  );

  /**
   * Renders a static (non-editable) TextField for ReadOnly field types in editable modes.
   * Used for dimensions like Reporting Currency that have a fixed value.
   * @param config - The KPI dimension configuration
   * @param entry - The driver display option entry containing the static value
   */
  const renderStaticField = useCallback(
    (config: KpiDimensionConfig, entry: DriverDisplayOptionEntry) => (
      <TextField
        key={config.key}
        label={config.label}
        value={entry.value || ''}
        size='small'
        fullWidth
        slotProps={{ input: { readOnly: true } }}
      />
    ),
    [],
  );

  /**
   * Renders a dimension row for `dimensionSelect` mode.
   * Renders a DimensionPickerDropdown for DimensionPicker types, a Select for Select types,
   * or a static TextField for ReadOnly types.
   * @param config - The KPI dimension configuration determining the field type
   * @param entry - The driver display option entry containing the current value
   */
  const renderDimensionSelectRow = useCallback(
    (config: KpiDimensionConfig, entry: DriverDisplayOptionEntry) => {
      if (config.baseKpiField.type === BaseKpiDimensionFieldTypes.DimensionPicker) {
        const { dimensionName, rootNodesFilter } = config.baseKpiField;
        const optionalDimensionPickerContextProps: Partial<DimensionPickerDropdownProps['contextProps']> = {};

        if (!config.decklineDrivers?.hierarchyAccessor || !config.decklineDrivers?.valueAccessor) {
          return renderStaticField(config, entry);
        }
        const selectedNodeId = convertKpiDriverValuesToDimSumNodeId(
          {
            [config.decklineDrivers.hierarchyAccessor]: entry.hierarchy,
            [config.decklineDrivers.valueAccessor]: entry.value,
          } as Partial<AnyKPIDrivers>,
          config,
        );

        if (rootNodesFilter) {
          optionalDimensionPickerContextProps.providedRootNodes = rootNodesFilter;
        }

        return (
          <DimensionPickerDropdown
            key={config.key}
            label={config.label}
            contextProps={{ dimension: dimensionName, ...optionalDimensionPickerContextProps }}
            onSelectionChange={(node) => handleDropdownChange(config, node)}
            selectedNodeId={selectedNodeId || undefined}
            disabled={disabled}
            searchable
          />
        );
      }

      if (config.baseKpiField.type === BaseKpiDimensionFieldTypes.Select) {
        const { options } = config.baseKpiField as BaseKpiDimensionFieldTypeSelect;
        return (
          <FormControl key={config.key} fullWidth size='small'>
            <InputLabel>{config.label}</InputLabel>
            <Select
              value={entry.value || ''}
              onChange={(e) => handleSelectChange(config, e)}
              label={config.label}
              disabled={disabled}
            >
              <MenuItem value=''>
                <em>None</em>
              </MenuItem>
              {options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label || option.value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      }

      // ReadOnly field type
      return renderStaticField(config, entry);
    },
    [disabled, handleDropdownChange, handleSelectChange, renderStaticField],
  );

  /**
   * Renders a dimension row for `dimensionPicker` mode.
   * DimensionPicker types show a readonly TextField with an edit button that opens a dialog.
   * Select types render an inline Select. ReadOnly types render a static TextField.
   * @param config - The KPI dimension configuration determining the field type
   * @param entry - The driver display option entry containing the current value and active state
   */
  const renderDimensionPickerRow = useCallback(
    (config: KpiDimensionConfig, entry: DriverDisplayOptionEntry) => {
      const placeholderValue =
        !entry.active && placeholderDrivers && config.decklineDrivers
          ? (placeholderDrivers[config.decklineDrivers.valueAccessor] ?? '')
          : '';

      if (config.baseKpiField.type === BaseKpiDimensionFieldTypes.DimensionPicker) {
        return (
          <Box key={config.key} className={`${BASE_CLASS}__row`}>
            <TextField
              label={config.label}
              value={entry.active ? entry.value || '' : ''}
              placeholder={placeholderValue as string}
              size='small'
              fullWidth
              slotProps={{
                inputLabel: placeholderValue ? { shrink: true } : undefined,
                input: { readOnly: true },
              }}
            />
            {entry.active && (
              <IconButton size='small' onClick={() => handleEditClick(config)} disabled={disabled}>
                <EditIcon fontSize='small' />
              </IconButton>
            )}
          </Box>
        );
      }

      if (config.baseKpiField.type === BaseKpiDimensionFieldTypes.Select) {
        const { options } = config.baseKpiField as BaseKpiDimensionFieldTypeSelect;
        return (
          <FormControl key={config.key} fullWidth size='small'>
            <InputLabel shrink={!!placeholderValue || undefined}>{config.label}</InputLabel>
            <Select
              value={entry.active ? entry.value || '' : ''}
              onChange={(e) => handleSelectChange(config, e)}
              label={config.label}
              disabled={disabled || !entry.active}
              displayEmpty={!entry.active && !!placeholderValue}
              renderValue={
                !entry.active && placeholderValue
                  ? () => <span className={`${BASE_CLASS}__placeholder`}>{placeholderValue}</span>
                  : undefined
              }
            >
              <MenuItem value=''>
                <em>None</em>
              </MenuItem>
              {options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label || option.value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      }

      // ReadOnly field type
      return renderStaticField(config, entry);
    },
    [disabled, handleEditClick, handleSelectChange, renderStaticField, placeholderDrivers],
  );

  /**
   * Renders a single dimension row based on the current mode and switch visibility.
   * Delegates to the appropriate row renderer (readonly, dimensionSelect, or dimensionPicker)
   * and optionally wraps the row with a toggle switch.
   * @param config - The KPI dimension configuration for the row to render
   */
  const renderRow = useCallback(
    (config: KpiDimensionConfig) => {
      const entry = driverOptions[config.key];
      if (!entry) return null;

      const showSwitch = !hideSwitches && renderMode !== 'readonly';

      let rowContent: ReactNode;
      if (renderMode === 'readonly') {
        rowContent = renderReadonlyRow(config, entry);
      } else if (renderMode === 'dimensionSelect') {
        rowContent = renderDimensionSelectRow(config, entry);
      } else {
        rowContent = renderDimensionPickerRow(config, entry);
      }

      if (!showSwitch) return rowContent;

      return (
        <Box key={config.key} className={`${BASE_CLASS}__switch-row`}>
          <Box className={`${BASE_CLASS}__switch-row-content`}>{rowContent}</Box>
          <Switch
            edge='end'
            checked={entry.active}
            onChange={(e) => onToggleSwitch?.(config.key, e.target.checked)}
            size='small'
            disabled={disabled}
          />
        </Box>
      );
    },
    [
      driverOptions,
      hideSwitches,
      renderMode,
      renderReadonlyRow,
      renderDimensionSelectRow,
      renderDimensionPickerRow,
      onToggleSwitch,
      disabled,
    ],
  );

  /**
   * Computes the preSelectedNodes array for the DimensionPickerDialog
   * based on the currently editing dimension's hierarchy and value.
   */
  const dialogPreSelectedNodes = useMemo(() => {
    if (isNil(editingKey)) return undefined;
    const entry = driverOptions[editingKey];
    if (entry?.hierarchy && entry?.value) {
      return [`${entry.hierarchy}-${entry.value}`];
    }
    return undefined;
  }, [editingKey, driverOptions]);

  /**
   * Additional props for <DimensionPickerDialogWithProvider> which only need to be conditionally included
   */
  const optionalDimensionPickerDialogProps = useMemo(() => {
    const optionalProps: Partial<DimensionPickerDialogWithProviderProps> = {};

    if (dialogRootNodes) {
      optionalProps.providedRootNodes = dialogRootNodes;
    }
    return optionalProps;
  }, [dialogRootNodes]);

  return (
    <Box className={rootClassName}>
      {renderMode === 'readonly' ? (
        <List className='segments'>{dimensionList.map((config) => renderRow(config))}</List>
      ) : (
        <Stack spacing={1.5}>{dimensionList.map((config) => renderRow(config))}</Stack>
      )}

      {renderMode === 'dimensionPicker' && (
        <DimensionPickerDialogWithProvider
          key={`${editingKey}-${dialogPreSelectedNodes?.join(',')}`}
          open={dialogOpen && !!dialogDimension}
          dimension={dialogDimension}
          preSelectedNodes={dialogPreSelectedNodes}
          title='Dimension Hierarchy'
          onClose={handleDialogClose}
          onConfirm={handleDialogConfirm}
          {...optionalDimensionPickerDialogProps}
        />
      )}
    </Box>
  );
};

export default KPIDriversDisplay;
